import {
  API_DELETE_ALL_READ_NOTIFICATIONS,
  API_DELETE_MULTIPLE_NOTIFICATIONS,
  API_DELETE_NOTIFICATION,
  API_GET_NOTIFICATIONS,
  API_MS_ASSET_REQUESTS,
  API_UPDATE_ALL_NOTIFICATION_AS_READ,
  API_UPDATE_MULTIPLE_NOTIFICATION_AS_READ,
  API_UPDATE_NOTIFICATION_AS_READ,
} from "@/constants/apiEndpoints";
import { createApi } from "@reduxjs/toolkit/query/react";
import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import type { IApiGeneralParams, IApiPaginatedResponse } from "@/types";
import type {
  INotificationResponse,
  ISingleNotification,
  IUnreadNotificationCount,
} from "@/types/notifications.interface";
import { setUnreadNotificationsCount } from "@/store/slices/user.slice";

export const notificationsApi = createApi({
  reducerPath: "notificationsApi",
  baseQuery: dynamicBaseQuery,
  refetchOnFocus: false,
  tagTypes: [
    "getAllNotifications",
    "getUnreadNotificationsCount",
    "getNotifications",
  ],
  endpoints: (builder) => ({
    getAllNotifications: builder.query<
      IApiPaginatedResponse<INotificationResponse>,
      IApiGeneralParams & { platformSectionType: string }
    >({
      query: (params) => ({
        url: API_GET_NOTIFICATIONS,
        method: "GET",
        params,
      }),
      providesTags: ["getAllNotifications"],
      onQueryStarted: async (_body, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          const { unreadCount } = data.data;
          dispatch(setUnreadNotificationsCount(unreadCount));
        } catch (e: any) {
          console.error(
            e?.message ??
              e?.data?.message ??
              "Something went wrong. Please try again later.",
          );
        }
      },
    }),
    getUnreadNotificationsCount: builder.query<IUnreadNotificationCount, void>({
      query: () => ({
        url: API_MS_ASSET_REQUESTS,
        method: "GET",
      }),
      providesTags: ["getUnreadNotificationsCount"],
    }),
    getNotificaitonById: builder.query<ISingleNotification, number>({
      query: (notificationId) => ({
        url: `${API_GET_NOTIFICATIONS}/${notificationId}`,
        method: "GET",
      }),
    }),
    updateSingleNotificationAsRead: builder.mutation<
      IApiPaginatedResponse<void>,
      number
    >({
      query: (notificationId) => {
        return {
          url: API_UPDATE_NOTIFICATION_AS_READ(notificationId),
          method: "PATCH",
        };
      },
      invalidatesTags: ["getAllNotifications", "getNotifications"],
    }),
    updateMultipleNotificationAsRead: builder.mutation<
      IApiPaginatedResponse<void>,
      { ids: [] }
    >({
      query: (body) => {
        return {
          url: API_UPDATE_MULTIPLE_NOTIFICATION_AS_READ,
          method: "PATCH",
          body,
        };
      },
      invalidatesTags: ["getAllNotifications", "getNotifications"],
    }),
    updateAllNotificationAsRead: builder.mutation<
      IApiPaginatedResponse<void>,
      void
    >({
      query: () => {
        return {
          url: API_UPDATE_ALL_NOTIFICATION_AS_READ,
          method: "PATCH",
        };
      },
      invalidatesTags: ["getAllNotifications", "getNotifications"],
    }),
    deleteNotificationById: builder.mutation<
      IApiPaginatedResponse<void>,
      { notificationId: number }
    >({
      query: ({ notificationId }) => ({
        url: API_DELETE_NOTIFICATION(notificationId),
        method: "DELETE",
      }),
      invalidatesTags: ["getAllNotifications", "getNotifications"],
    }),
    deleteAllReadNotifications: builder.mutation<
      IApiPaginatedResponse<void>,
      void
    >({
      query: () => ({
        url: API_DELETE_ALL_READ_NOTIFICATIONS,
        method: "DELETE",
      }),
      invalidatesTags: ["getAllNotifications", "getNotifications"],
    }),
    deleteMultipleNotifications: builder.mutation<
      IApiPaginatedResponse<void>,
      { ids: [] }
    >({
      query: (body) => ({
        url: API_DELETE_MULTIPLE_NOTIFICATIONS,
        method: "POST",
        body,
      }),
      invalidatesTags: ["getAllNotifications", "getNotifications"],
    }),
    getNotifications: builder.infiniteQuery<
      IApiPaginatedResponse<INotificationResponse>,
      { limit: number; platformSectionType: string },
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (result) => {
          if (!result?.meta) return undefined;
          const { currentPage, totalPages } = result.meta;
          return currentPage < totalPages ? currentPage + 1 : undefined;
        },
      },
      query: ({ queryArg, pageParam }) => ({
        url: API_GET_NOTIFICATIONS,
        method: "GET",
        params: {
          limit: queryArg.limit,
          platformSectionType: queryArg.platformSectionType,
          page: pageParam,
        },
      }),
      providesTags: ["getNotifications"],
      onQueryStarted: async (_body, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          const { unreadCount } = data.pages[0].data;
          dispatch(setUnreadNotificationsCount(unreadCount));
        } catch (e: any) {
          console.error(
            e?.message ??
              e?.data?.message ??
              "Something went wrong. Please try again later.",
          );
        }
      },
    }),
  }),
});

export const {
  useGetAllNotificationsQuery,
  useGetUnreadNotificationsCountQuery,
  useGetNotificaitonByIdQuery,
  useUpdateAllNotificationAsReadMutation,
  useUpdateMultipleNotificationAsReadMutation,
  useUpdateSingleNotificationAsReadMutation,
  useDeleteAllReadNotificationsMutation,
  useDeleteMultipleNotificationsMutation,
  useDeleteNotificationByIdMutation,
  useGetNotificationsInfiniteQuery,
} = notificationsApi;
